/**
 * This device's identity for helm-api device approval (SPEC §20). Pure functions over a
 * `PlatformEnv` snapshot so they are testable without a browser; `readPlatformEnv()` takes the
 * snapshot from the real globals.
 */

/** Values the API accepts for a web client (`devices.platform`, SPEC §19). */
export type DevicePlatform = "pwa_desktop" | "pwa_ios" | "pwa_android" | "browser";

export type PlatformEnv = {
  /** Running as an installed app (`display-mode: standalone` or iOS `navigator.standalone`). */
  standalone: boolean;
  userAgent: string;
  /** iPadOS reports a Mac user agent; touch points tell them apart. */
  maxTouchPoints: number;
};

const ANDROID = /android/i;
const IOS = /iphone|ipad|ipod/i;
const MAC = /macintosh|mac os x/i;

function isIos(env: PlatformEnv): boolean {
  return IOS.test(env.userAgent) || (MAC.test(env.userAgent) && env.maxTouchPoints > 1);
}

/** Installed PWAs are told apart by OS; anything in a browser tab is `browser`. */
export function detectPlatform(env: PlatformEnv): DevicePlatform {
  if (!env.standalone) return "browser";
  if (ANDROID.test(env.userAgent)) return "pwa_android";
  if (isIos(env)) return "pwa_ios";
  return "pwa_desktop";
}

function osName(env: PlatformEnv): string {
  const ua = env.userAgent;
  if (ANDROID.test(ua)) return "Android";
  if (/iphone|ipod/i.test(ua)) return "iPhone";
  // iPadOS 13+ sends a Mac user agent; touch points give it away.
  if (/ipad/i.test(ua) || (MAC.test(ua) && env.maxTouchPoints > 1)) return "iPad";
  if (/windows/i.test(ua)) return "Windows";
  if (MAC.test(ua)) return "Mac";
  if (/cros/i.test(ua)) return "ChromeOS";
  if (/linux/i.test(ua)) return "Linux";
  return "unknown OS";
}

function browserName(ua: string): string {
  // Order matters: Edge and Opera also say "Chrome"; Chrome also says "Safari".
  if (/edg\//i.test(ua)) return "Edge";
  if (/opr\//i.test(ua)) return "Opera";
  if (/firefox|fxios/i.test(ua)) return "Firefox";
  if (/chrome|crios/i.test(ua)) return "Chrome";
  if (/safari/i.test(ua)) return "Safari";
  return "Browser";
}

/** A readable default name, e.g. "Chrome on Windows" or "HELM app on iPhone" (max 80 chars). */
export function defaultDeviceName(env: PlatformEnv): string {
  const who = env.standalone ? "TradePrizm app" : browserName(env.userAgent);
  return `${who} on ${osName(env)}`.slice(0, 80);
}

export const PLATFORM_LABELS: Record<string, string> = {
  pwa_desktop: "Desktop app",
  pwa_ios: "iPhone / iPad app",
  pwa_android: "Android app",
  browser: "Browser",
  native_desktop: "Desktop (native)",
  native_ios: "iOS (native)",
  native_android: "Android (native)",
};

export function platformLabel(platform: string): string {
  return PLATFORM_LABELS[platform] ?? platform;
}

/** Snapshot of the real browser globals (safe outside a browser). */
export function readPlatformEnv(): PlatformEnv {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { standalone: false, userAgent: "", maxTouchPoints: 0 };
  }
  const nav = navigator as Navigator & { standalone?: boolean };
  const standalone =
    (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) ||
    nav.standalone === true;
  return { standalone, userAgent: nav.userAgent, maxTouchPoints: nav.maxTouchPoints };
}

/** What this browser keeps to re-attach to its approved device on the next sign-in. */
export type StoredDevice = { id: string; secret: string };

export const DEVICE_STORE_KEY = "device";

export function parseStoredDevice(raw: string | null): StoredDevice | null {
  if (!raw) return null;
  try {
    const v: unknown = JSON.parse(raw);
    if (typeof v !== "object" || v === null) return null;
    const { id, secret } = v as Record<string, unknown>;
    return typeof id === "string" && typeof secret === "string" && id && secret ? { id, secret } : null;
  } catch {
    return null;
  }
}
